import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// Models and Types
import { WeeklyPlanStateData } from "@/models";

// Enums
import { AssignmentType, Entity, Grade } from "@/enums";

// Store
import { selectWeeklyPlanData } from "@/store/slices";

// Services
import { getUserData } from "@/services/API";

// Hooks
import { usePreTeachData } from "@/hooks";

// Components
import { WizardBody, WizardFooter, WizardHeader, Step, SummaryCard, BadgeWithTooltip } from "@/features/wizard/components";
import { Tooltip, Placeholder } from "@/components/Global";
// import { RolloverFollowUp } from "@/features/planner/components";
import PrerequisiteCreateModal from "./PrerequisiteCreateModal/PrerequisiteCreateModal";

// Context
import { WizardContext } from "@/features/wizard/WizardContext";

// Icons
import { RolloverIcon, SkilltreeIcon, WeekIcon, EditIcon, QuestionIcon } from "@/features/planner/icons";

// Styles
import "./RolloverSteps.scss";

export type RolloverWizardCompoundComponent = React.FC<{
  steps?: string[];
  currentStep?: number;
  onNext?: () => void;
}> & {
  // RolloverFollowUp: React.FC;
  PrepareUpcoming: React.FC;
  Finalize: React.FC;
};

const RolloverSteps: RolloverWizardCompoundComponent = ({
  steps: stepsOverride,
  currentStep: thisStep,
  onNext,
}) => {
  // Create Step objects with icons
  const wizardSteps: Step[] = [
    {
      label: "Plan Reteach",
      icon: (color: string) => (
        <div style={{ color }}>
          <RolloverIcon width={24} height={24} color={color} />
        </div>
      ),
    },
    {
      label: "Plan Pre-teach",
      icon: (color: string) => (
        <div style={{ color }}>
          <SkilltreeIcon width={24} height={24} color={color} />
        </div>
      ),
    },
    {
      label: "Plan Teach",
      icon: (color: string) => (
        <div style={{ color }}>
          <WeekIcon width={24} height={24} color={color} />
        </div>
      ),
    },
  ];

  const [steps] = useState(
    stepsOverride ?? [
      "Review Last Week",
      "Prepare for Upcoming Lessons",
      "Finalize Plan",
    ]
  );

  const navigate = useNavigate();
  const weeklyPlanData = useSelector(
    selectWeeklyPlanData
  ) as WeeklyPlanStateData & {
    currentStep: number;
    manifest: string[];
    parentId: string;
  };
  const currentStep =
    thisStep !== undefined ? thisStep : weeklyPlanData.currentStep;
  const roster = useSelector((state: any) => state.reports?.roster);
  const { userId } = getUserData();
  const classroomId = roster?.ui?.selections?.classroom?.id;

  const handleNext = () => {
    if (onNext) {
      onNext();
    } else {
      currentStep < steps.length - 1 ? {} : handleCreatePlan();
    }
  };

  const handlePrevious = () => currentStep > 0 && {};
  const handleCancel = () => {
    navigate("/dashboard", { replace: true });
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <div>Disabled Rollover Follow Up</div>;
        // return <RolloverFollowUp />;
      case 1:
        return <PrepareUpcoming />;
      case 2:
        return <Finalize />;
    }
  };

  const handleCreatePlan = () => {
    const { dateFrom, dateTo, manifest, parentId, curriculumInfo } =
      weeklyPlanData as WeeklyPlanStateData & {
        currentStep: number;
        manifest: string[];
        parentId: string;
      };
    if (userId && manifest && manifest.length > 0) {
      const payload = {
        entityId: classroomId,
        assignor: userId,
        entityType: Entity.CLASS,
        assignmentType: AssignmentType.CURRICULUM,
        grade: Grade.KINDERGARTEN,
        dateFrom,
        dateTo,
        parentId,
        curriculumInfo,
        manifest,
      };

      // dispatch(createPlan({ assignment: payload }) as any);
    }
  };

  return (
    <section className="section-wizard section-wizard-planner">
      <WizardContext.Provider value={{ steps, currentStep }}>
        <WizardHeader steps={wizardSteps} currentStep={currentStep} />
        {renderStep()}
        <WizardFooter
          onNext={handleNext}
          onPrevious={handlePrevious}
          onCancel={handleCancel}
        />
      </WizardContext.Provider>
    </section>
  );
};

const PrepareUpcoming: React.FC = () => {
  const { isLoading, prerequisiteSkills, redKids, students }
   = usePreTeachData();
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [selectedSkillData, setSelectedSkillData] = useState<any>(null);

  const [nextWeekMonday, nextWeekFriday] = useMemo(() => {
    const monday = moment().add(1, "week").isoWeekday(1);
    return [monday, moment(monday).add(4, "days")];
  }, []);

  const [transformedData, setTransformedData] = useState<any[]>([]);

  useEffect(() => {
    if (!isLoading && prerequisiteSkills.length > 0) {
      const initialTransformedData = prerequisiteSkills
        .map((focusSkill) => {
          const studentsForSkill = redKids
            .filter((kid) => kid.focusSkill.skillId === focusSkill.skillId)
            .map((kid) => ({
              studentId: kid.student.id,
              studentName: `${kid.student.firstName} ${kid.student.lastName}`,
              prerequisites: {
                ...kid.underdevelopedPrerequisite,
                exposureCount: kid.exposureCount,
                errorCount: kid.errorCount,
                pacing: kid.pacing,
              },
            }));

          return {
            focusSkill: {
              skillId: focusSkill.skillId,
              displayName: focusSkill.displayName,
              standards: focusSkill.standards,
              source: focusSkill.source,
            },
            prerequisites: focusSkill.prerequisites || [],
            students: studentsForSkill,
          };
        })
        .filter(Boolean);

      setTransformedData(initialTransformedData);
    }
  }, [isLoading, prerequisiteSkills, redKids]);

  const handleEditSkill = (skillData: any) => {
    setSelectedSkillData(skillData);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedSkillData(null);
  };

  const handleAddStudent = (prerequisiteSkillId: string, student: any) => {
    setTransformedData(prevData =>
      prevData.map(skillEntry => {
        if (skillEntry.focusSkill.skillId === prerequisiteSkillId) {
          return {
            ...skillEntry,
            students: [...skillEntry.students, student]
          };
        }
        return skillEntry;
      })
    );
  };

  const handleRemoveStudent = (prerequisiteSkillId: string, studentId: string) => {
    setTransformedData(prevData =>
      prevData.map(skillEntry => {
        if (skillEntry.focusSkill.skillId === prerequisiteSkillId) {
          return {
            ...skillEntry,
            students: skillEntry.students.filter((s: any) => s.studentId !== studentId)
          };
        }
        return skillEntry;
      })
    );
  };

  return (
    <WizardBody>
      <fieldset>
        <div className="prepare-upcoming-container">
          <div className="header">
            <div className="question-icon">
              <QuestionIcon width={32} height={32} cursor="pointer" />
            </div>
            <span className="title">
              Pre-Teach for Upcoming Week{" "}
              {`${nextWeekMonday.format("MMM. D")} - ${nextWeekFriday.format(
                "MMM. D"
              )}`}
            </span>
            <span className="subtitle">
              Some students need additional instruction before they will be
              ready for next week's skills.
            </span>
          </div>

          <div className="contents">
            <div className="smart-summary">
              <span className="title">Smart Summary</span>
              <div className="summary-cards">
                {isLoading ? (
                  <>
                    <Placeholder
                      animation="wave"
                      heightInRem={8}
                      colWidth={12}
                    />
                    <Placeholder
                      animation="wave"
                      heightInRem={8}
                      colWidth={12}
                    />
                  </>
                ) : (
                  <>
                    <SummaryCard
                      title="Ready to Learn"
                      titleColor="#047857"
                      backgroundColor="#BEFBDD80"
                      borderColor="#BEFBDD80"
                      secondValue={students.length}
                      firstValue={
                        students.length -
                        [...new Set(redKids.map((kid) => kid.student.id))]
                          .length
                      }
                    />
                    <SummaryCard
                      title="Need Prerequisites"
                      titleColor="#B40909"
                      borderColor="#fdc9d1"
                      backgroundColor="#F146681A"
                      secondValue={students.length}
                      firstValue={
                        [...new Set(redKids.map((kid) => kid.student.id))]
                          .length
                      }
                    />
                  </>
                )}
              </div>
            </div>
            <div className="readiness-gaps">
              <span className="title">Readiness Gaps to Address</span>
              <div className="table-container">
                {isLoading ? (
                  <div className="loading-placeholder">
                    <Placeholder
                      animation="wave"
                      heightInRem={12}
                      colWidth={12}
                    />
                  </div>
                ) : (
                  <div className="table">
                    <div className="table-header">
                      <div className="table-row">
                        <div className="table-cell">
                          Skills from Upcoming Lessons
                        </div>
                        <div className="table-cell">
                          Need Prerequisite Skills{" "}
                          <Tooltip
                            type="custom"
                            content="Students who haven't mastered important prerequisite skills for the objective skill appear in this list."
                          />
                        </div>
                        <div className="table-cell">Edit</div>
                      </div>
                    </div>

                    <div className="table-body">
                      {transformedData.map((item, index) => (
                        <div key={index} className="table-row">
                          <div
                            className={`table-cell${index === transformedData.length - 1 ? " last-row" : ""}`}
                          >
                            {item?.focusSkill?.displayName}
                          </div>
                          <div
                            className={`table-cell${index === transformedData.length - 1 ? " last-row" : ""}`}
                          >
                            {item?.students.length > 0 ? (
                              <div className="students-column">
                                {item.students.map((student: any) => {
                                  const fullName = student.studentName;
                                  const displayName = item.focusSkill.displayName;
                                  return (
                                    <BadgeWithTooltip
                                      key={student.studentId}
                                      {...{
                                        fullName,
                                        firstName: fullName.split(" ")[0],
                                        lastName: fullName.split(" ")[1],
                                        displayName: item.focusSkill.displayName,
                                        errorCount: student.prerequisites.errorCount,
                                        exposureCount: student.prerequisites.exposureCount,
                                        masteryNow: student.prerequisites.pacing || "BELOW_GRADE_LEVEL",
                                        showIcon: true,
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="none-badge">None</span>
                            )}
                          </div>
                          <div
                            className={`table-cell edit-cell${index === transformedData.length - 1 ? " last-row" : ""}`}
                          >
                            <div className="edit-icon-container">
                              <EditIcon
                                width={50}
                                height={50}
                                color="#80089D"
                                onClick={() => handleEditSkill(item)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <PrerequisiteCreateModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            skillData={selectedSkillData}
            onAddStudent={handleAddStudent}
            onRemoveStudent={handleRemoveStudent}
          />
        </div>
      </fieldset>
    </WizardBody>
  );
};

const Finalize: React.FC = () => {
  return (
    <WizardBody>
      <h3>Finalize Your Rollover Plan</h3>
      <div className="d-flex gap-5">Placeholder Text</div>
    </WizardBody>
  );
};

// RolloverSteps.RolloverFollowUp = RolloverFollowUp;
RolloverSteps.PrepareUpcoming = PrepareUpcoming;
RolloverSteps.Finalize = Finalize;

export default RolloverSteps;
export { RolloverSteps };
